import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Database,
  Download,
  PanelLeftClose,
  PanelLeft,
  Menu,
  X,
  UserCog,
  Smartphone,
  ClipboardList,
  User,
  HelpCircle,
  BarChart3,
  HeartPulse,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { SignOutButton } from "@/components/signout-user";
import { EdgeCareBrand } from "@/components/edgecare-brand";

const baseNavItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/study-health", icon: HeartPulse, label: "Study Health" },
  { to: "/participants", icon: Users, label: "Participants" },
  { to: "/explorer", icon: Database, label: "Data Explorer" },
  { to: "/visualization", icon: BarChart3, label: "Visualization" },
  { to: "/export", icon: Download, label: "Export" },
  { to: "/help", icon: HelpCircle, label: "Help" },
] as const;

const adminNavItems = [
  { to: "/admin/users", icon: UserCog, label: "User Management" },
  { to: "/admin/devices", icon: Smartphone, label: "Devices" },
  { to: "/admin/survey-config", icon: ClipboardList, label: "Survey Config" },
] as const;

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAdmin, username } = useAuth();

  return (
    <>
      {/* Mobile header bar */}
      <div className="fixed top-0 left-0 right-0 z-40 flex h-14 items-center border-b bg-background px-4 md:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(true)}
        >
          <Menu className="size-5" />
        </Button>
        <EdgeCareBrand className="ml-3" />
      </div>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "flex h-screen flex-col border-r bg-background transition-all duration-200",
          // Desktop: inline sidebar
          "max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:w-64 max-md:shadow-lg",
          // Mobile: slide in/out
          mobileOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full",
          // Desktop: collapsible width
          collapsed ? "md:w-14" : "md:w-56"
        )}
      >
        <div
          className={cn(
            "flex h-14 items-center border-b px-3",
            collapsed ? "md:justify-center" : "justify-between"
          )}
        >
          {/* Mobile: always show title + close button */}
          <EdgeCareBrand
            className={cn(
              collapsed && "md:hidden"
            )}
          />

          {/* Mobile close button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen(false)}
          >
            <X className="size-4" />
          </Button>

          {/* Desktop collapse button */}
          <Button
            variant="ghost"
            size="icon"
            className="max-md:hidden"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? (
              <PanelLeft className="size-4" />
            ) : (
              <PanelLeftClose className="size-4" />
            )}
          </Button>
        </div>

        <nav className="flex flex-1 flex-col gap-1 p-2">
          {baseNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  collapsed && "md:justify-center md:px-0",
                  isActive
                    ? "bg-[#2563EB]/10 text-[#2563EB]"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )
              }
            >
              <item.icon className="size-4 shrink-0" />
              <span className={cn(collapsed && "md:hidden")}>
                {item.label}
              </span>
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div className={cn("my-2 border-t", collapsed && "md:hidden")} />
              <span className={cn("px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider", collapsed && "md:hidden")}>
                Admin
              </span>
              {adminNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      collapsed && "md:justify-center md:px-0",
                      isActive
                        ? "bg-[#2563EB]/10 text-[#2563EB]"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )
                  }
                >
                  <item.icon className="size-4 shrink-0" />
                  <span className={cn(collapsed && "md:hidden")}>
                    {item.label}
                  </span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="border-t p-2">
          <div className={cn("flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground", collapsed && "md:justify-center md:px-0")}>
            <User className="size-4 shrink-0" />
            <span className={cn("truncate", collapsed && "md:hidden")}>{username}</span>
          </div>
          <SignOutButton collapsed={collapsed} />
        </div>
      </aside>
    </>
  );
}
